import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import PageHeader1 from '../../components/common/PageHeader1';
import { usePermissions } from '../../context/AuthContext';
import toast from 'react-hot-toast';

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

function BookingList() {
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [selectedBookingType, setSelectedBookingType] = useState('All');
  const [selectedBookingStatus, setSelectedBookingStatus] = useState('All');
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [searchValue, setSearchValue] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const debouncedStartDate = useDebounce(startDate, 500);
  const debouncedEndDate = useDebounce(endDate, 500);
  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterExcludeHiddenColumns: true,
    quickFilterValues: [''],
  });
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('vedhvidhi-admin-token');
  const STATIC_PASSWORD = 'vedhvidhi@123';

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [rowCount, setRowCount] = useState(0);

  const statusOptions = [
    'accepted', 'rejected', 'cancelled', 'pending',
    'ongoing', 'completed', 'missed', 'disConnected'
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const apiPage = paginationModel.page + 1;
        const limit = paginationModel.pageSize;

        const bookingTypeParam =
          selectedBookingType !== 'All'
            ? `&type=${selectedBookingType}`
            : '';

        const bookingStatusParam =
          selectedBookingStatus !== 'All'
            ? `&status=${selectedBookingStatus}`
            : '';

        const searchParam = debouncedSearchValue ? `&search=${debouncedSearchValue}` : '';
        const startDateParam = debouncedStartDate ? `&startDate=${debouncedStartDate}` : '';
        const endDateParam = debouncedEndDate ? `&endDate=${debouncedEndDate}` : '';

        const requestUrl = `${process.env.REACT_APP_BASEURL}/admin/booking?page=${apiPage}&limit=${limit}${bookingTypeParam}${bookingStatusParam}${searchParam}${startDateParam}${endDateParam}`;
        const response = await axios.get(requestUrl, {
          headers: { Authorization: token },
        });

        const bookings = response?.data?.data || [];
        const pagination = response?.data?.paginationDetail || {};

        const data = bookings.map((item, index) => {
          const formatCamelCase = (str) => {
            if (!str) return '';
            return str
              .replace(/([a-z])([A-Z])/g, '$1 $2')
              .replace(/^./, (char) => char.toUpperCase());
          };

          return {
            sono: paginationModel.page * paginationModel.pageSize + index + 1,
            orderId: item?.orderId,
            userName: item?.userName,
            bookingId: item?.bookingId,
            astroName: item?.astroName,
            bookingTypeRaw: item?.bookingType,
            bookingType: formatCamelCase(item?.bookingType),
            bookingStatus: item?.bookingStatus,
            totalDuration:
              Number(item?.actualDeduction || 0) +
              Number(item?.promotionalDeduction || 0),
            actualDeduction:
              item?.actualDeduction != null
                ? `${Number(item.actualDeduction).toFixed(2)}`
                : 'N/A',
            promoDeduction:
              item?.promotionalDeduction != null
                ? `${Number(item.promotionalDeduction).toFixed(2)}`
                : 'N/A',
            createdAt: item?.createdAt
              ? new Date(item?.createdAt).toLocaleString()
              : '',
          };
        });

        setRows(data);
        setFilteredRows(data);
        setRowCount(pagination?.totalDocs || 0);
      } catch (error) {
        console.error('Error fetching data:', error);
        setRows([]);
        setFilteredRows([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchData();
    } else {
      setLoading(false);
      setRows([]);
      setFilteredRows([]);
      setRowCount(0);
    }
  }, [paginationModel.page, paginationModel.pageSize, selectedBookingType, selectedBookingStatus, debouncedSearchValue, debouncedStartDate, debouncedEndDate, token]);

  const handlePaginationModelChange = (newModel) => {
    if (newModel.pageSize !== paginationModel.pageSize) {
      setPaginationModel({ page: 0, pageSize: newModel.pageSize });
    } else {
      setPaginationModel(newModel);
    }
  };

  const deleteBooking = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_BASEURL}/admin/booking/${id}`, {
        headers: { Authorization: token },
      });
      setRows((prev) => prev.filter((row) => row.bookingId !== id));
      setFilteredRows((prev) => prev.filter((row) => row.bookingId !== id));
      setRowCount((prevCount) => (prevCount > 0 ? prevCount - 1 : 0));
      if (rows.length === 1 && paginationModel.page > 0) {
        setPaginationModel((prev) => ({ ...prev, page: prev.page - 1 }));
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleClickOpen = (userId) => {
    setSelectedUserId(userId);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUserId(null);
  };

  const handleDelete = async () => {
    if (selectedUserId) {
      await deleteBooking(selectedUserId);
    }
    handleClose();
  };

  const handleViewClick = (bookingId) => {
    setSelectedBookingId(bookingId);
    setPasswordDialogOpen(true);
    setPasswordInput('');
  };

  const handlePasswordDialogClose = () => {
    setPasswordDialogOpen(false);
    setPasswordInput('');
    setShowPassword(false);
    setSelectedBookingId(null);
  };

  const handlePasswordSubmit = async () => {
    if (!passwordInput) {
      toast.error('Please enter password');
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/chat-password/verify`,
        {
          chatPassword: passwordInput
        },
        {
          headers: { Authorization: token }
        }
      );

      if (response.data.verified) {
        if (selectedBookingId) {
          navigate(`/booking-details/${selectedBookingId}`);
        }
        handlePasswordDialogClose();
      } else {
        toast.error(response.data.message || 'Incorrect password. Please try again.');
        setPasswordInput('');
      }
    } catch (error) {
      console.error('Password verification error:', error);
      toast.error(error.response?.data?.message || 'Incorrect password. Please try again.');
      setPasswordInput('');
    }
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      const bookingTypeParam = selectedBookingType !== 'All' ? `&type=${selectedBookingType}` : '';
      const bookingStatusParam = selectedBookingStatus !== 'All' ? `&status=${selectedBookingStatus}` : '';
      const searchParam = searchValue ? `&search=${searchValue}` : '';
      const startDateParam = startDate ? `&startDate=${startDate}` : '';
      const endDateParam = endDate ? `&endDate=${endDate}` : '';

      // Fetch all data for export
      const requestUrl = `${process.env.REACT_APP_BASEURL}/admin/booking?${bookingTypeParam}${bookingStatusParam}${searchParam}${startDateParam}${endDateParam}`.replace(/^\?&/, '?');
      const response = await axios.get(requestUrl, {
        headers: { Authorization: token },
      });

      const bookings = response?.data?.data || [];
      if (bookings.length > 0) {
        const headers = ['S.No', 'Order ID', 'User Name', 'Astrologer Name', 'Booking Type', 'Booking Status', 'Total Deduction', 'Actual Deduction', 'Promotional Deduction', 'Created At'];
        const csvContent = [
          headers.join(','),
          ...bookings.map((item, index) => [
            index + 1,
            `"${item?.orderId || ''}"`,
            `"${item?.userName || ''}"`,
            `"${item?.astroName || ''}"`,
            `"${item?.bookingType || ''}"`,
            `"${item?.bookingStatus || ''}"`,
            Number(item?.actualDeduction || 0) + Number(item?.promotionalDeduction || 0),
            item?.actualDeduction != null ? Number(item.actualDeduction).toFixed(2) : '0.00',
            item?.promotionalDeduction != null ? Number(item.promotionalDeduction).toFixed(2) : '0.00',
            item?.createdAt ? new Date(item?.createdAt).toLocaleString() : '',
          ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `bookings_${new Date().getTime()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Bookings exported successfully!');
      } else {
        toast.warning('No data to export');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSelectedBookingType('All');
    setSelectedBookingStatus('All');
    setSearchValue('');
    setStartDate('');
    setEndDate('');
    setPaginationModel({ page: 0, pageSize: 10 });
  };

  const columns = [
    { field: 'sono', headerName: 'S.NO', width: 80 },
    { field: 'orderId', headerName: 'Order ID', width: 170 },
    { field: 'userName', headerName: 'User Name', width: 200 },
    { field: 'astroName', headerName: 'Astrologer Name', width: 200 },
    { field: 'bookingType', headerName: 'Booking Type', width: 180 },
    { field: 'bookingStatus', headerName: 'Booking Status', width: 180 },
    { field: 'totalDuration', headerName: 'Total Deduction', width: 180 },
    { field: 'actualDeduction', headerName: 'Actual Deduction', width: 180 },
    { field: 'promoDeduction', headerName: 'Promotional Deduction', width: 200 },
    { field: 'createdAt', headerName: 'Created At', width: 200 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: ({ row }) => (
        <>
          <Tooltip title="View Details">
            <IconButton onClick={() => handleViewClick(row.bookingId)}>
              <i className="icofont-eye text-info"></i>
            </IconButton>
          </Tooltip>
          {can('bookings:delete') && (
            <Tooltip title="Delete Booking">
              <IconButton onClick={() => handleClickOpen(row.bookingId)}>
                <i className="icofont-ui-delete text-danger" style={{ fontSize: '20px' }}></i>
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
  ];
  const currentDate = new Date();
  const minDate = new Date(currentDate);
  minDate.setDate(minDate.getDate() - 1);
  const tomorrow = new Date(currentDate);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDateString = minDate.toISOString().split('T')[0];
  const tomorrowString = tomorrow.toISOString().split('T')[0];

  return (
    <div className="body d-flex py-1">
      <div className="container-xxl">
        <PageHeader1
          righttitle="Add Booking"
          link="/booking-add"
          routebutton={can('bookings:add')}
          pagetitle='Booking Management List'
        />
        <div className="row g-0 mb-3">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body d-flex gap-3 flex-wrap align-items-end">
                <TextField
                  label="Search"
                  placeholder="Search by name, email, phone..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  size="small"
                  sx={{ width: '250px' }}
                />

                <TextField
                  type="date"
                  label="Start Date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ width: '200px' }}
                  inputProps={{
                    max: tomorrowString
                  }}
                />

                <TextField
                  type="date"
                  label="End Date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ width: '200px' }}
                  inputProps={{
                    max: tomorrowString
                  }}
                />

                <FormControl sx={{ minWidth: 200 }} size="small">
                  <InputLabel>Booking Type</InputLabel>
                  <Select
                    value={selectedBookingType}
                    label="Booking Type"
                    onChange={(e) => {
                      setSelectedBookingType(e.target.value);
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="chat">Chat</MenuItem>
                    <MenuItem value="call">Call</MenuItem>
                    <MenuItem value="videocall">Video Call</MenuItem>
                    <MenuItem value="physicalvisit">Physical Visit</MenuItem>
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 200 }} size="small">
                  <InputLabel>Booking Status</InputLabel>
                  <Select
                    value={selectedBookingStatus}
                    label="Booking Status"
                    onChange={(e) => {
                      setSelectedBookingStatus(e.target.value);
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                  >
                    <MenuItem value="All">All</MenuItem>
                    {statusOptions.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button variant="contained" color="success" onClick={handleExport} sx={{ height: '40px' }}>
                  Export
                </Button>
                <Button variant="outlined" color="secondary" onClick={handleClearFilters} sx={{ height: '40px' }}>
                  Clear
                </Button>
              </div>

              <div style={{ height: 600, width: '100%' }}>
                <DataGrid
                  rows={filteredRows}
                  columns={columns}
                  getRowId={(row) => row.bookingId}
                  loading={loading}
                  rowCount={rowCount}
                  paginationMode="server"
                  paginationModel={paginationModel}
                  onPaginationModelChange={handlePaginationModelChange}
                  pageSizeOptions={[10, 25, 50, 100]}
                  slots={{ toolbar: GridToolbar }}
                  filterModel={filterModel}
                  onFilterModelChange={(newModel) => setFilterModel(newModel)}
                  slotProps={{ toolbar: { showQuickFilter: true } }}
                  columnVisibilityModel={columnVisibilityModel}
                  onColumnVisibilityModelChange={(newModel) =>
                    setColumnVisibilityModel(newModel)
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>Delete Confirmation</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this booking?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleDelete} color="error">Delete</Button>
          </DialogActions>
        </Dialog>

        {/* Password Protection Dialog */}
        <Dialog open={passwordDialogOpen} onClose={handlePasswordDialogClose}>
          <DialogTitle>Enter Password</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Please enter the password to view booking details.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              variant="outlined"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handlePasswordSubmit();
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? (
                        <i className="icofont-eye-blocked" style={{ fontSize: '20px' }}></i>
                      ) : (
                        <i className="icofont-eye" style={{ fontSize: '20px' }}></i>
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
            <Button
              variant="text"
              color="secondary"
              size="small"
              onClick={() => navigate('/booking-password/Forget')}
              sx={{ textTransform: 'none' }}
            >
              Forget Password?
            </Button>
            <Box>
              <Button onClick={handlePasswordDialogClose} sx={{ mr: 1 }}>Cancel</Button>
              <Button onClick={handlePasswordSubmit} variant="contained" color="primary">
                Submit
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}

export default BookingList;